<template>
  <personalize-preview
    v-if="previewOnly"
    :base-tag-svg-img-path="baseSvgImgPath"
    :pet-name="personalizationDetails.name"
    :phone-number="personalizationDetails.phone || ''"
    :home-safe-id="selectedPet && selectedPet.idCode ? selectedPet.idCode : 'U237JC9Z'"
    :show-labels="previewShowLabels"
    :height="previewHeight"
    :scale="previewScale"
    :personalization-selected="personalizationDetails.selected"
    :left-zone-line1="leftZoneLine1"
    :left-zone-line2="leftZoneLine2"
    :hotline-number="hotlineNumber"
    :tag-back-url="tagBackUrl"
    :default-phone-number="defaultPhoneNumber"
  />
  <section v-else class="mt-2">
    <personalize-preview
      v-if="!hidePreview"
      :base-tag-svg-img-path="baseSvgImgPath"
      :pet-name="personalizationDetails.name"
      :phone-number="personalizationDetails.phone || ''"
      :home-safe-id="selectedPet && selectedPet.idCode ? selectedPet.idCode : 'U237JC9Z'"
      :show-labels="previewShowLabels"
      :height="previewHeight"
      :scale="previewScale"
      :personalization-selected="personalizationDetails.selected"
      :left-zone-line1="leftZoneLine1"
      :left-zone-line2="leftZoneLine2"
      :hotline-number="hotlineNumber"
      :tag-back-url="tagBackUrl"
      :default-phone-number="defaultPhoneNumber"
    />
    <h2 class="font-weight-bold fs-lg-24 fs-18" v-if="Number(price) > 0">{{ $t(!isAdmin ? 'personalize.title.default' : 'personalize.title.admin', [Number(price)]) }}</h2>
    <h2 class="font-weight-bold fs-lg-24 fs-18" v-else>{{ $t(!isAdmin ? 'personalize.title.free' : 'personalize.title.adminFree') }}</h2>
    <b-form-group id="personalized-checkbox-group" label-for="product-style-personalized">
      <div>
        <b-form-checkbox
          id="product-style-personalized"
          v-model="personalizationDetails.selected"
          v-bind="testIdAttr('tag-personalize-input')"
          size="lg"
          style="z-index: 1029; display: grid"
          @input="$emit('input', personalizationDetails)"
          @change="onPersonalizationSelectionChange"
        >
          <h2
            class="text-primary fs-16 align-self-center align-items-center pt-2"
            :class="{
              'text-light': !personalizationDetails.selected && personalizationDetails.selected !== null,
            }"
          >
            {{ $t(!isAdmin ? 'personalize.option.default' : 'personalize.option.admin') }}
          </h2>
        </b-form-checkbox>
        <section v-if="personalizationDetails.selected" class="mt-3">
          <b-row>
            <b-col cols="12" xl="6">
              <DpSelect
                v-model="personalizationDetails.name"
                :options="petNameOptions"
                :rules="{ required: dropDownPetNameSelected }"
                label="Pet's Name"
                vid="c-pet-name-select"
                @input="handlePetNameSelect"
              />
              <div class="mt-3">
                <dp-input
                  v-if="!dropDownPetNameSelected"
                  id="personalize-name"
                  v-bind="testIdAttr('tag-personalize-name-input')"
                  v-model.trim="personalizationDetails.name"
                  :value="personalizationDetails.name"
                  name="pet's name"
                  type="text"
                  placeholder="Pet's name"
                  :validation-rules="{ required: true, noEmojis: true, max: 9 }"
                  vid="c-pet-name-select"
                  :validation-state="validated ? 'valid' : null"
                  @input="setName"
                />
              </div>
            </b-col>
            <b-col cols="12" xl="6">
              <DpSelect
                v-model="personalizationDetails.phone"
                :options="phoneNumberOptions"
                :rules="{ required: dropDownPhoneNumberSelected }"
                label="Contact Number"
                vid="c-contact-number"
                @input="handleContactNumberSelect"
              />
              <div class="mt-3">
                <dp-input
                  v-if="!dropDownPhoneNumberSelected"
                  id="personalize-phone"
                  v-bind="testIdAttr('tag-personalize-phone-input')"
                  type="tel"
                  name="phone number"
                  inputmode="numeric"
                  autocomplete="tel"
                  vid="c-contact-number"
                  validation-rules="required|phoneNumber"
                  pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                  :value="formattedValue"
                  :placeholder="$t('personalize.option.name')"
                  :formatter="phoneNumberFormatter"
                  :aria-label="$t('personalize.option.name')"
                  :validation-state="validated ? 'valid' : null"
                  @input="setPhone"
                />
              </div>
            </b-col>
          </b-row>
        </section>
        <span
          v-if="
            pet &&
            personalizationDetails.selected &&
            personalizationDetails.name &&
            pet.name.toLowerCase().slice(0, 9) !== personalizationDetails.name.toLowerCase().slice(0, 9)
          "
          class="text-warning"
        >
          {{ $t('personalize.option.warning') }}
        </span>
      </div>
    </b-form-group>
    <ValidationProvider v-slot="{ errors }" vid="p-personalization-select" :rules="{ required: true }" slim>
      <input v-model="personalizationDetails.selected" type="text" class="d-none" hidden />
      <span v-if="errors && errors[0]" class="text-danger">{{ $t('personalize.option.error') }}</span>
    </ValidationProvider>
  </section>
</template>

<script>
import DpInput from '@/components/library/Input';
import DpSelect from './library/Select';
import PersonalizePreview from './PersonalizePreview';
import { PersonalizationMixins } from '@/mixins/components/personalization';
import { testIdAttr } from '@docupet/component-library';

export default {
  name: 'Personalize',
  components: {
    PersonalizePreview,
    DpSelect,
    DpInput,
  },
  mixins: [PersonalizationMixins],
  props: {
    validated: {
      type: Boolean,
      required: true,
      default: null,
    },
    leftZoneLine1: {
      type: String,
      default: 'Scan here',
      required: false,
    },
    leftZoneLine2: {
      type: String,
      default: null,
      required: false,
    },
    hotlineNumber: {
      type: String,
      default: '',
      required: false,
    },
    tagBackUrl: {
      type: String,
      default: 'www.docupet.com',
      required: false,
    },
  },
  computed: {
    petNameOptions() {
      const options = [{ value: '', text: 'Select Pet Name' }];
      if (this.petOwner) {
        this.petOwner?.pets?.forEach((pet) => {
          options.push({ value: `${pet.name?.slice(0, 9)}`, text: `${pet.name}` });
        });
      }
      options.push({ value: 'EnterPetName', text: "Enter pet's name" });
      return options;
    },
  },
  mounted() {
    if (this.pet && this.personalizationDetails.selected && this.personalizationDetails.name) {
      this.personalizationDetails.name = this.personalizationDetails.name.slice(0, 9);
    }
  },
  methods: {
    // Bound via v-bind (not the v-testid directive) so the attribute reaches the
    // underlying <input> through $attrs. Both dp-input and b-form-checkbox set
    // inheritAttrs: false, so a directive would land on the wrapper instead.
    testIdAttr,
  },
};
</script>

<style lang="scss" scoped>
.custom-control h2 {
  padding-top: 2px;
}
.personalize-img {
  margin-left: -2rem;
}
.personalize-text {
  text-overflow: scale;
}
p {
  font-size: 1.3rem;
  line-height: 1.3;
}

/* Mobile first */
.fs-18 {
  font-size: 18px;
}

/* Desktop and larger */
@media (min-width: 768px) {
  .fs-lg-24 {
    font-size: 24px;
  }
}

/* Always 16px */
.fs-16 {
  font-size: 16px;
}
</style>
